import { Container, Flex, Text, VStack, Box, HStack, Image, Grid, GridItem, Input, InputGroup, InputLeftAddon, InputRightElement, Button, Spinner, Skeleton, Tooltip, IconButton, useToast } from "@chakra-ui/react";
import { InfoOutlineIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { ThemeColor } from "../theme/palette";
import "../styles/MintBox.css";
import "../styles/partnerDrip.css";
import { useState, useEffect, useMemo, useDeferredValue } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useNativeBalance } from "../hooks/useNativeBalance";
import { useBalance } from "../hooks/useBalance";
import { useDripDeposit } from "../hooks/useDripDeposit";
import { useKyrosApy } from "../hooks/useKyrosApy";
import { solAmountFormatter } from "../helpers/formatter";
import { formatSmartFixed6 } from "../utils/format";
import { useContext } from "react";
import { AppConfigContext } from "../context";
import { PublicKey } from "@solana/web3.js";
import { useDripPosition } from "../hooks/useDripPosition";
import { useDripPositions } from "../hooks/useDripPositions";
import { useDripRedeem } from "../hooks/useDripRedeem";
import { useDripHistory } from "../hooks/useDripHistory";
import { useKyrosTokens } from "../hooks/useKyrosTokens";
import { DripPositionsCard } from "../components/DripPositionsCard/DripPositionsCard";
import { DripPageHero } from "../features/drip/DripPageHero";
import { DripPartnerHero } from "../features/drip/DripPartnerHero";
import { PartnerBackground } from "../features/drip/PartnerBackground";
import { PartnerTokenIcon } from "../features/drip/PartnerTokenIcon";
import { DripSegmentTabs } from "../features/drip/DripSegmentTabs";
import { DripInputTokenPicker } from "../features/drip/DripInputTokenPicker";
import { DripWithdrawPercentSelector } from "../features/drip/DripWithdrawPercentSelector";
import { DripRewardsChart } from "../features/drip/DripRewardsChart";
import { DripAlerts } from "../features/drip/DripAlerts";
import {
  SuspendedCampaignNotice,
  hasSuspendedCampaignToken,
} from "../features/drip/SuspendedCampaignNotice";
import {
  incentiveDecimalsByMint,
  incentivePayoutsToHistory,
  mergeIncentiveTotals,
} from "../features/drip/mergeDripIncentives";
import { useMintDecimals } from "../hooks/useMintDecimals";
import { BalanceMaxHeader, StakeAmountField } from "../features/stake/StakeAmountField";
import { TokenPill } from "../features/stake/TokenPill";
import { DRIP_BOX_BG, DRIP_BORDER, DRIP_TEXT_LABEL, DRIP_TEXT_MUTED, DRIP_TEXT_SECONDARY, dripSurfaceSx } from "../features/drip/dripStyles";
import {
  MINT_TO_TOKEN,
  YIELD_TOKEN_IMAGES,
  YIELD_TOKEN_CONTRACTS,
} from "../constants/dripTokens";
import { DripPartner, PARTNER_ICON_BY_MINT } from "../constants/partners";
import { describeWalletError } from "../hooks/utils/walletErrors";
import { useTokenIcon } from "../hooks/useTokenIcon";
import { useDripVaults } from "../hooks/useDripVaults";
import { useDripVaultState } from "../hooks/useDripVaultState";
import { useDripVaultIcons } from "../features/drip/useDripVaultIcons";
import { findVaultForInputAsset, findVaultById } from "../constants/dripVaults";
import { DripInputAssetOption } from "../features/drip/DripInputTokenPicker";
import { computeSharesForTokensRaw } from "../features/drip/dripValue";
import { mergeDripPositions } from "../features/drip/aggregateDripPositions";

type RewardTokenOption = {
  symbol: string;
  mint: string;
  icon: string;
  name?: string;
  mcap?: number;
};

const MAX_RENDERED_SEARCH_RESULTS = 15;
// ZEC is the default reward token and gets the large featured card; everything
// else (including KYROS) sits in the smaller suggested grid below it.
const FEATURED_SYMBOL = "ZEC";
const SUGGESTED_SECONDARY_SYMBOLS = ["KYROS", "ZBTC", "XAUt0"] as const;

interface DripPageProps {
  /** When set, locks the reward token to a single partner asset and re-skins the page. */
  partner?: DripPartner;
}

export const DripPage = ({ partner }: DripPageProps = {}) => {
  const { publicKey } = useWallet();
  const { balance: solBalance, fetchNativeBalance } = useNativeBalance();
  const { balance: tokenBalance, fetchBalance: fetchTokenBalance } = useBalance();

  const [selectedAsset, setSelectedAsset] = useState<string>("SOL");
  const [selectedAssetImage, setSelectedAssetImage] = useState<string>("/solana.png");

  const dripVaults = useDripVaults();
  // Vault token images come from the registry (see useDripVaultIcons), so no
  // site on this page branches on a vault id to pick a logo.
  const vaultIcons = useDripVaultIcons();

  // The withdraw tab targets a position, not an input asset. With one position
  // this stays implicit, preserving the single-vault experience.
  const [withdrawVaultId, setWithdrawVaultId] = useState<string | null>(null);
  const { positions: allPositions, isLoading: isAllPositionsLoading, fetchPositions } = useDripPositions();

  // SOL and JitoSOL are conversion inputs rather than vaults, so their icons are
  // bundled here; every vault's own deposit asset comes from the registry, keyed
  // by its symbol so a new LST needs no entry.
  const inputAssetImages: Record<string, string | undefined> = useMemo(
    () => ({
      SOL: "/solana.png",
      JitoSOL: "/jitosol.png",
      ...Object.fromEntries(
        dripVaults.map((vault) => [vault.symbol, vaultIcons[vault.id]])
      ),
    }),
    [dripVaults, vaultIcons]
  );

  const inputAssetOptions: DripInputAssetOption[] = useMemo(
    () =>
      dripVaults.flatMap((vault) =>
        vault.inputAssets.map((asset) => ({
          symbol: asset,
          image: inputAssetImages[asset] ?? "",
        }))
      ),
    [dripVaults, inputAssetImages]
  );

  // The withdraw tab targets a position, so its options are what the wallet
  // actually holds rather than the registry's input assets. Position symbols are
  // deposit-asset symbols, so they share inputAssetImages' keys.
  const withdrawAssetOptions: DripInputAssetOption[] = useMemo(
    () =>
      allPositions.map((p) => ({
        symbol: p.symbol,
        image: inputAssetImages[p.symbol] ?? "",
      })),
    [allPositions, inputAssetImages]
  );

  // The selected input asset determines which vault we transact against.
  const activeVault =
    findVaultForInputAsset(dripVaults, selectedAsset) ?? dripVaults[0];
  const minDeposit = activeVault.minDeposit;

  const { deposit, updateRewardToken, loading: depositLoading, currentStep } = useDripDeposit(activeVault);
  const { apy: kyrosApy, isLoading: isApyLoading } = useKyrosApy();
  const { jitoSolMintAddress, solVrtMintAddress } = useContext(AppConfigContext);
  const { position, isLoading: isPositionLoading, hasPosition, fetchPosition } = useDripPosition(activeVault);
  const withdrawPosition =
    (withdrawVaultId
      ? allPositions.find((p) => p.vaultId === withdrawVaultId)
      : undefined) ??
    allPositions[0] ??
    position;
  const withdrawVault =
    findVaultById(dripVaults, withdrawPosition?.vaultId ?? "") ?? activeVault;
  const withdrawVaultIcon = vaultIcons[withdrawVault.id];
  // Minimums are per vault, and the withdraw tab targets a position rather than
  // the deposit-selected vault, so it must not read the deposit vault's minimum.
  const withdrawMinDeposit = withdrawVault.minDeposit;
  const { redeem: redeemFromWithdrawVault, loading: withdrawRedeemLoading } =
    useDripRedeem(withdrawVault);
  const {
    vaultState: activeVaultState,
    isLoading: isActiveVaultStateLoading,
    fetchVaultState,
  } = useDripVaultState(activeVault);
  const {
    vaultState: fetchedWithdrawVaultState,
    isLoading: isFetchedWithdrawVaultStateLoading,
    fetchVaultState: fetchWithdrawVaultState,
  } = useDripVaultState(withdrawVault);
  // Withdrawing from the deposit-selected vault is the common case, and both
  // hooks would then read the same account plus its mint plus a price. Reuse
  // the active read instead of duplicating it.
  const isWithdrawVaultActive = withdrawVault.id === activeVault.id;
  const withdrawVaultState = isWithdrawVaultActive
    ? activeVaultState
    : fetchedWithdrawVaultState;
  const isWithdrawVaultStateLoading = isWithdrawVaultActive
    ? isActiveVaultStateLoading
    : isFetchedWithdrawVaultStateLoading;
  const { tokens: kyrosTokens, isLoading: isLoadingKyrosTokens } = useKyrosTokens();
  const toast = useToast();

  const SOL_MINT_ADDRESS = "So11111111111111111111111111111111111111112";
  const MODERATE_APY = kyrosApy?.sol || 0.07; // Use kySOL APY from API, fallback to 7%

  // Partner pages (e.g. /ansem) lock the reward token and re-skin the accent.
  const isPartnerPage = !!partner;
  const accentColor = partner?.accent ?? ThemeColor.KAIROS;
  const accentRgb = partner?.accentRgb ?? "95, 244, 244";
  const accentBg = (alpha: number) => `rgba(${accentRgb}, ${alpha})`;
  const partnerMetadataIcon = useTokenIcon(partner?.tokenMint);
  const partnerIconSources = [partner?.iconUrl, partnerMetadataIcon];
  // Keeps the partner's own reward token on-brand in the chart, instead of
  // whatever palette slot its rank happens to land in.
  const chartAccentByMint = useMemo(
    () => (partner ? { [partner.tokenMint]: partner.accent } : undefined),
    [partner]
  );
  // Example target asset used in the "What is Drip?" FAQ answer.
  const dripExampleAsset = partner?.tokenSymbol ?? FEATURED_SYMBOL;

  const [selectedBuyToken, setSelectedBuyToken] = useState<RewardTokenOption | null>(() =>
    partner
      ? {
          symbol: partner.tokenSymbol,
          mint: partner.tokenMint,
          icon: partner.iconUrl ?? "",
          name: partner.tokenName,
        }
      : {
          symbol: "ZEC",
          mint: YIELD_TOKEN_CONTRACTS.ZEC,
          icon: YIELD_TOKEN_IMAGES.ZEC,
          name: "Zcash",
        }
  );
  const [amount, setAmount] = useState<string>("");
  const [displayedBalance, setDisplayedBalance] = useState<string>("0 SOL");
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [rewardTokenPrice, setRewardTokenPrice] = useState<number | null>(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState<boolean>(false);
  const [expectedRewards, setExpectedRewards] = useState<{
    solPerYear: number;
    usdPerYear: number;
    rewardTokenPerYear: number;
    rewardTokenMint: string;
  } | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState<string>("");
  const [assetSearchQuery, setAssetSearchQuery] = useState<string>("");
  const [activeTab, setActiveTab] = useState(0);
  const deferredSearchQuery = useDeferredValue(assetSearchQuery);

  useEffect(() => {
    if (publicKey) {
      fetchNativeBalance(publicKey);
      fetchPosition(publicKey);
      fetchPositions(publicKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, activeVault.id]);

  useEffect(() => {
    fetchVaultState();
  }, [fetchVaultState]);

  useEffect(() => {
    // Skipped when the withdraw vault IS the active vault: activeVaultState
    // already holds that read. The hook itself stays unconditional.
    if (isWithdrawVaultActive) return;
    fetchWithdrawVaultState();
  }, [isWithdrawVaultActive, fetchWithdrawVaultState]);

  useEffect(() => {
    if (publicKey && selectedAsset !== "SOL") {
      const mintAddress =
        selectedAsset === "JitoSOL"
          ? jitoSolMintAddress
          : selectedAsset === "kySOL"
            ? solVrtMintAddress
            : activeVault.depositMint;
      fetchTokenBalance(new PublicKey(mintAddress), publicKey);
    }
  }, [publicKey, selectedAsset, jitoSolMintAddress, solVrtMintAddress, activeVault.depositMint, fetchTokenBalance]);

  useEffect(() => {
    if (selectedAsset === "SOL") {
      setDisplayedBalance(solAmountFormatter.format(Number(solBalance)) + " SOL");
    } else {
      setDisplayedBalance(solAmountFormatter.format(Number(tokenBalance)) + ` ${selectedAsset}`);
    }
  }, [solBalance, tokenBalance, selectedAsset]);

  useEffect(() => {
    if (hasPosition && !selectedBuyToken && position?.rewardTokenMint) {
      setSelectedBuyToken({
        symbol: position.rewardTokenName ?? "TOKEN",
        mint: position.rewardTokenMint,
        icon: position.rewardTokenImage || "",
        name: position.rewardTokenName ?? undefined,
      });
    }
  }, [hasPosition, position?.rewardTokenMint, position?.rewardTokenName, position?.rewardTokenImage, selectedBuyToken]);

  const defaultRewardTokenName = "KYROS";
  const effectiveRewardTokenName = partner
    ? partner.tokenSymbol
    : selectedBuyToken?.symbol ??
      (hasPosition
        ? (position?.rewardTokenName ?? null)
        : defaultRewardTokenName);
  const effectiveRewardTokenMintAddress = partner
    ? partner.tokenMint
    : selectedBuyToken?.mint
      ? selectedBuyToken.mint
      : hasPosition
        ? position?.rewardTokenMint
        : YIELD_TOKEN_CONTRACTS.KYROS;
  const effectiveRewardTokenImage = partner
    ? (partner.iconUrl || partnerMetadataIcon || "")
    : (effectiveRewardTokenMintAddress ? PARTNER_ICON_BY_MINT[effectiveRewardTokenMintAddress] : undefined) ||
      selectedBuyToken?.icon ||
      (effectiveRewardTokenName && effectiveRewardTokenName in YIELD_TOKEN_IMAGES
        ? YIELD_TOKEN_IMAGES[effectiveRewardTokenName as keyof typeof YIELD_TOKEN_IMAGES]
        : undefined) ||
      position?.rewardTokenImage ||
      "";
  const amountNumber = Number(amount);
  const hasPositiveAmount = !Number.isNaN(amountNumber) && amountNumber > 0;
  const hasExistingPosition = hasPosition && !!position;
  // The 1-SOL minimum applies to the resulting position, not to each deposit:
  // topping up an existing position by any amount is allowed as long as the
  // total stays >= 1 SOL. A first deposit still needs to be >= 1 SOL.
  const existingPositionKySol = hasExistingPosition ? position.tokenValue : 0;
  const projectedTotalPosition = existingPositionKySol + (hasPositiveAmount ? amountNumber : 0);
  const isBelowMinimumAmount = hasPositiveAmount && projectedTotalPosition < minDeposit;
  const withdrawableAtUiPrecision = withdrawPosition
    ? Math.floor(withdrawPosition.tokenValue * 1e6) / 1e6
    : 0;
  // dripFetch already caps tokenValue at what the wallet holds; these flags only
  // separate "the claim was destroyed" from "no position" and from an unreadable
  // read, since none of the three deserves the same copy. Raw shares rather than
  // the UI-rounded value: a dust remainder must not read as a destroyed claim.
  const isWithdrawClaimShort = !!withdrawPosition?.isClaimShort;
  const hasWithdrawableShares =
    !!withdrawPosition && BigInt(withdrawPosition.withdrawableSharesRaw || "0") > BigInt(0);
  const isWithdrawClaimDestroyed = isWithdrawClaimShort && !hasWithdrawableShares;
  const hasWithdrawablePosition =
    !!withdrawPosition &&
    Number.isFinite(withdrawPosition.tokenValue) &&
    withdrawableAtUiPrecision > 0 &&
    // Nothing is redeemable against a destroyed claim, whatever a stale
    // tokenValue says.
    !isWithdrawClaimDestroyed;
  const hasVisiblePosition =
    hasExistingPosition && (position.tokenValueUsd === null || position.tokenValueUsd >= 1);
  // A withdrawal must leave at least the withdrawn vault's minimum in the
  // position, unless the user is withdrawing everything (which also covers
  // exiting a position that is already below the minimum).
  const withdrawAmountNumber = parseFloat(withdrawAmount);
  const hasPositiveWithdrawAmount = !Number.isNaN(withdrawAmountNumber) && withdrawAmountNumber > 0;
  const isFullWithdrawal =
    hasPositiveWithdrawAmount && withdrawAmountNumber >= withdrawableAtUiPrecision;
  const remainingAfterWithdrawal = withdrawableAtUiPrecision - withdrawAmountNumber;
  const isBelowWithdrawMinimum =
    hasPositiveWithdrawAmount &&
    !isFullWithdrawal &&
    remainingAfterWithdrawal < withdrawMinDeposit;
  const featuredYieldToken: RewardTokenOption = {
    symbol: "ZEC",
    mint: YIELD_TOKEN_CONTRACTS.ZEC,
    icon: YIELD_TOKEN_IMAGES.ZEC,
    name: "Zcash",
  };
  const tokenMetadataBySymbol = useMemo(() => (
    kyrosTokens.reduce<Record<string, RewardTokenOption>>((acc, token) => {
      const key = token.symbol.toLowerCase();
      if (!acc[key]) {
        acc[key] = {
          symbol: token.symbol,
          mint: token.id,
          icon: token.icon,
          name: token.name,
          mcap: token.mcap,
        };
      }
      return acc;
    }, {})
  ), [kyrosTokens]);
  const allSearchableTokens = useMemo(() => (
    kyrosTokens
      // The featured token has its own card; kySOL is never a selectable reward (it's the fallback only).
      .filter((token) => token.symbol.toUpperCase() !== FEATURED_SYMBOL)
      .filter((token) => token.id !== solVrtMintAddress && token.symbol.toLowerCase() !== "kysol")
      .map((token) => ({
        symbol: token.symbol,
        mint: token.id,
        icon: token.icon,
        name: token.name,
        mcap: token.mcap,
      }))
  ), [kyrosTokens, solVrtMintAddress]);
  const suggestedSecondaryYieldTokens = useMemo(() => (
    SUGGESTED_SECONDARY_SYMBOLS
      .map((symbol) => tokenMetadataBySymbol[symbol.toLowerCase()] || {
        symbol,
        mint: YIELD_TOKEN_CONTRACTS[symbol as keyof typeof YIELD_TOKEN_CONTRACTS],
        icon: YIELD_TOKEN_IMAGES[symbol as keyof typeof YIELD_TOKEN_IMAGES],
      })
      .filter((token): token is RewardTokenOption => !!token)
  ), [tokenMetadataBySymbol]);
  const normalizedSearchQuery = deferredSearchQuery.trim().toLowerCase();
  const tokenMetadataByMint = useMemo(() => (
    kyrosTokens.reduce<Record<string, RewardTokenOption>>((acc, token) => {
      if (!acc[token.id]) {
        acc[token.id] = {
          symbol: token.symbol,
          mint: token.id,
          icon: token.icon,
          name: token.name,
          mcap: token.mcap,
        };
      }
      return acc;
    }, {})
  ), [kyrosTokens]);
  const positionForDisplay = useMemo(() => {
    if (!position) return null;
    const bundledIcon = PARTNER_ICON_BY_MINT[position.rewardTokenMint];
    const apiToken = tokenMetadataByMint[position.rewardTokenMint];
    if (!apiToken && !bundledIcon) return position;

    return {
      ...position,
      rewardTokenName: apiToken?.symbol || position.rewardTokenName,
      // Bundled partner asset wins over remote metadata icons, which for meme
      // reward tokens (e.g. ANSEM) can be broken third-party URLs.
      rewardTokenImage: bundledIcon || apiToken?.icon || position.rewardTokenImage,
    };
  }, [position, tokenMetadataByMint]);
  // The unified card shows every vault the wallet holds, so the enriched read of
  // the deposit-selected vault is folded into the all-vaults list rather than
  // rendered as a card of its own. An account with no shares left is not a
  // position, matching what useDripPositions keeps — except when the claim is
  // short, where dropping the row would leave the wallet with no explanation.
  const mergedPositions = useMemo(
    () =>
      mergeDripPositions(
        allPositions,
        positionForDisplay &&
          (positionForDisplay.sharesRaw > 0 || positionForDisplay.isClaimShort)
          ? positionForDisplay
          : null
      ),
    [allPositions, positionForDisplay]
  );
  /** Does this wallet hold anything in any vault? The page's own gate. */
  const hasAnyPosition = mergedPositions.length > 0;
  // What the wallet is actually dripping right now, across every vault — the
  // basis for notices tied to a reward token rather than to a page.
  const heldRewardTokenMints = useMemo(
    () => mergedPositions.map((p) => p.rewardTokenMint),
    [mergedPositions]
  );
  const { visibleSecondaryYieldTokens, totalSearchMatches } = useMemo(() => {
    if (!normalizedSearchQuery) {
      return {
        visibleSecondaryYieldTokens: suggestedSecondaryYieldTokens,
        totalSearchMatches: suggestedSecondaryYieldTokens.length,
      };
    }

    const matches = allSearchableTokens.filter((token) => {
      const searchFields = [token.symbol, token.name]
        .filter(Boolean)
        .map((field) => field.toLowerCase());

      return searchFields.some((field) => field.includes(normalizedSearchQuery));
    }).sort((a, b) => (b.mcap ?? 0) - (a.mcap ?? 0));

    return {
      visibleSecondaryYieldTokens: matches.slice(0, MAX_RENDERED_SEARCH_RESULTS),
      totalSearchMatches: matches.length,
    };
  }, [normalizedSearchQuery, suggestedSecondaryYieldTokens, allSearchableTokens]);
  const hasActiveSearchQuery = normalizedSearchQuery.length > 0;
  const isSearchResultTruncated = hasActiveSearchQuery && totalSearchMatches > visibleSecondaryYieldTokens.length;
  const hasNoSearchResults = !isLoadingKyrosTokens && hasActiveSearchQuery && totalSearchMatches === 0;
  const searchResultHelperText = hasActiveSearchQuery
    ? `${totalSearchMatches.toLocaleString()} match${totalSearchMatches === 1 ? "" : "es"}`
    : "Suggested tokens";
  const shouldShowNoResultsState = !isLoadingKyrosTokens && visibleSecondaryYieldTokens.length === 0;
  const isFeaturedSelected = selectedBuyToken?.symbol.toUpperCase() === FEATURED_SYMBOL;
  const isOtherTokenSelected = !!selectedBuyToken && !isFeaturedSelected;
  const isFeaturedKyrosBoosted = featuredYieldToken.symbol.toUpperCase() === "KYROS";
  const isKyrosRewardToken = effectiveRewardTokenName === "KYROS";
  const rewardMultiplier = isKyrosRewardToken ? 2 : 1;

  const rewardTokenChanged =
    hasVisiblePosition &&
    !!effectiveRewardTokenMintAddress &&
    effectiveRewardTokenMintAddress !== position.rewardTokenMint;
  const isDefaultEstimateAmount = !hasPositiveAmount;
  const projectedKySolForEstimate = hasPositiveAmount
    ? (hasExistingPosition ? position.tokenValue : 0) + amountNumber
    : hasExistingPosition
      ? position.tokenValue
      : 1;
  const showBasedOnOneSolLabel = isDefaultEstimateAmount && !hasExistingPosition;
  const showBasedOnYourDepositLabel =
    isDefaultEstimateAmount && hasExistingPosition && position.tokenValue > 0;
  const shouldShowExpectedRewards =
    !!effectiveRewardTokenMintAddress &&
    projectedKySolForEstimate > 0 &&
    (hasPositiveAmount || rewardTokenChanged || isDefaultEstimateAmount);

  const handleMaxClick = () => {
    const currentBalance = selectedAsset === "SOL" ? solBalance : tokenBalance;
    const maxAmount = solAmountFormatter.format(Number(currentBalance));
    setAmount(maxAmount.replace(/,/g, ""));
  };

  const fetchPrices = async (rewardTokenAddress: string) => {
    setIsLoadingPrices(true);
    try {
      const ids = `${SOL_MINT_ADDRESS},${rewardTokenAddress}`;
      const response = await fetch(`https://lite-api.jup.ag/price/v3?ids=${ids}`);
      const data = await response.json();

      if (data[SOL_MINT_ADDRESS]) {
        setSolPrice(data[SOL_MINT_ADDRESS].usdPrice);
      }
      if (data[rewardTokenAddress]) {
        setRewardTokenPrice(data[rewardTokenAddress].usdPrice);
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  useEffect(() => {
    if (shouldShowExpectedRewards) {
      const rewardTokenAddress = effectiveRewardTokenMintAddress;
      if (rewardTokenAddress) {
        setExpectedRewards(null); // Clear previous rewards while loading
        fetchPrices(rewardTokenAddress);
      }
    } else {
      setSolPrice(null);
      setRewardTokenPrice(null);
      setExpectedRewards(null);
      setIsLoadingPrices(false);
    }
  }, [shouldShowExpectedRewards, effectiveRewardTokenMintAddress]);

  useEffect(() => {
    if (shouldShowExpectedRewards && solPrice && rewardTokenPrice) {
      if (projectedKySolForEstimate > 0) {
        // Show expected rewards for the projected total position, not only the incremental amount.
        const solPerYear = projectedKySolForEstimate * MODERATE_APY;
        // Convert to USD per year
        const usdPerYear = solPerYear * solPrice;
        // Convert to reward token per year
        const rewardTokenPerYear = usdPerYear / rewardTokenPrice;

        setExpectedRewards({
          solPerYear,
          usdPerYear,
          rewardTokenPerYear,
          rewardTokenMint: effectiveRewardTokenMintAddress,
        });
      } else {
        setExpectedRewards(null);
      }
    } else {
      setExpectedRewards(null);
    }
  }, [shouldShowExpectedRewards, projectedKySolForEstimate, solPrice, rewardTokenPrice, MODERATE_APY, effectiveRewardTokenMintAddress]);

  const hasFreshExpectedRewards =
    !!expectedRewards &&
    !!effectiveRewardTokenMintAddress &&
    expectedRewards.rewardTokenMint === effectiveRewardTokenMintAddress;
  // Rewards and alerts are per wallet, not per vault, so this must not be gated
  // on `hasVisiblePosition`: that only knows the deposit-selected vault, which
  // for an hSOL-only wallet is kySOL while SOL is the default input — leaving a
  // wallet that earns rewards with an empty chart and no alerts.
  const { data: dripHistory, isLoading: isHistoryLoading, refetch: refetchDripHistory } = useDripHistory(
    publicKey?.toBase58(),
    hasAnyPosition
  );
  const hasDripAlerts = (dripHistory?.alerts?.length ?? 0) > 0;
  const hasSuspendedNotice = hasSuspendedCampaignToken(heldRewardTokenMints);
  // Any position in any vault earns the "My position" tab, for the same reason.
  const useMyPositionTabs = hasAnyPosition;
  const tabLabels = useMyPositionTabs
    ? ["My position", "Update", "Withdraw"]
    : ["Deposit", "Withdraw"];
  const depositTabIndex = useMyPositionTabs ? 1 : 0;
  const withdrawTabIndex = useMyPositionTabs ? 2 : 1;

  useEffect(() => {
    if (activeTab >= tabLabels.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabLabels.length]);


  // Rewards display excludes kySOL: we never pay rewards in kySOL by design — a kySOL
  // payout only happens when something is wrong, and that is surfaced via the alerts banner.
  // Incentive-campaign payouts are reported separately and are absent from both
  // `history` and `totalDistributedRawByMint`, so they are merged in here —
  // every reward metric below derives from these two.
  const dripRewardHistory = useMemo(
    () =>
      [
        ...(dripHistory?.history ?? []),
        ...incentivePayoutsToHistory(dripHistory?.incentives),
      ].filter((item) => item.mint !== solVrtMintAddress),
    [dripHistory, solVrtMintAddress]
  );
  const dripRewardTotalsByMint = useMemo(() => {
    const totals = mergeIncentiveTotals(
      { ...(dripHistory?.totalDistributedRawByMint ?? {}) },
      dripHistory?.incentives
    );
    delete totals[solVrtMintAddress];
    return totals;
  }, [dripHistory, solVrtMintAddress]);

  // The incentive share of the totals above, so the chart can show which part
  // of a total came from the drip and which from a campaign.
  const dripIncentiveTotalsByMint = useMemo(
    () => mergeIncentiveTotals({}, dripHistory?.incentives),
    [dripHistory]
  );

  // Mints actually credited as rewards — used to resolve per-mint decimals.
  const dripRewardMints = useMemo(() => {
    const mints = new Set<string>();
    dripRewardHistory.forEach((item) => mints.add(item.mint));
    Object.keys(dripRewardTotalsByMint).forEach((mint) => mints.add(mint));
    return [...mints];
  }, [dripRewardHistory, dripRewardTotalsByMint]);

  const { decimalsByMint: onChainDecimalsByMint, isLoading: isDecimalsLoading } =
    useMintDecimals(dripRewardMints);

  // The on-chain lookup wins; the campaign's declared decimals cover the window
  // before it resolves, where the chart would otherwise fall back to 9 and draw
  // a 6-decimal reward 1000x too small.
  const decimalsByMint = useMemo(
    () => ({
      ...incentiveDecimalsByMint(dripHistory?.incentives),
      ...onChainDecimalsByMint,
    }),
    [dripHistory, onChainDecimalsByMint]
  );

  // Resolve a mint to a human-readable symbol (static list, then API metadata).
  const resolveRewardSymbol = (mint: string): string =>
    MINT_TO_TOKEN[mint] ??
    tokenMetadataByMint[mint]?.symbol ??
    `${mint.slice(0, 4)}...${mint.slice(-4)}`;

  const dripChartTokenMetaByMint = useMemo(() => {
    const meta: Record<string, { symbol: string; image?: string }> = {};

    const addMint = (mint: string) => {
      if (meta[mint]) return;
      const staticToken = MINT_TO_TOKEN[mint] ?? null;
      const apiToken = tokenMetadataByMint[mint] ?? null;
      meta[mint] = {
        symbol: staticToken ?? apiToken?.symbol ?? `${mint.slice(0, 4)}...${mint.slice(-4)}`,
        image:
          (staticToken ? YIELD_TOKEN_IMAGES[staticToken] : undefined) ||
          apiToken?.icon ||
          undefined,
      };
    };

    dripRewardHistory.forEach((item) => addMint(item.mint));
    Object.keys(dripRewardTotalsByMint).forEach(addMint);

    return meta;
  }, [dripRewardHistory, dripRewardTotalsByMint, tokenMetadataByMint]);

  const handleDeposit = async () => {
    if (!publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet to deposit.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!effectiveRewardTokenMintAddress) {
      toast({
        title: "No reward token selected",
        description: "Please select a reward token.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const rewardTokenMintAddress = effectiveRewardTokenMintAddress;
    const inputToken = selectedAsset;

    if (!hasPositiveAmount && hasVisiblePosition) {
      if (!rewardTokenChanged) {
        toast({
          title: "Nothing to update",
          description: "Enter an amount or pick a different reward token.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        await updateRewardToken(rewardTokenMintAddress);

        toast({
          title: "Reward token updated!",
          description: `Reward token changed to ${effectiveRewardTokenName ?? "selected token"}.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        if (publicKey) {
          fetchPosition(publicKey);
          fetchPositions(publicKey);
          refetchDripHistory();
        }
      } catch (err) {
        const { isRejection, message } = describeWalletError(
          err,
          "The reward token update transaction failed. Please try again."
        );
        toast({
          title: isRejection ? "Transaction cancelled" : "Update failed",
          description: message,
          status: isRejection ? "info" : "error",
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    try {
      await deposit(amount, inputToken, rewardTokenMintAddress);

      toast({
        title: "Deposit successful!",
        description: `Successfully deposited ${amount} ${selectedAsset} to drip${effectiveRewardTokenName ? ` ${effectiveRewardTokenName}` : ""}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Reset form
      setAmount("");
      // Refresh balance and position
      if (publicKey) {
        fetchNativeBalance(publicKey);
        fetchPosition(publicKey);
        fetchPositions(publicKey);
        refetchDripHistory();
      }
    } catch (err) {
      const { isRejection, message } = describeWalletError(
        err,
        "One of the drip transactions failed. Please try again."
      );
      toast({
        title: isRejection ? "Transaction cancelled" : "Deposit failed",
        description: message,
        status: isRejection ? "info" : "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleWithdraw = async () => {
    if (!publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet to withdraw.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!withdrawPosition) {
      toast({
        title: "No position found",
        description: "You don't have an active position.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Behind the disabled CTA: a read going stale between render and click
    // would otherwise revert after the user signed.
    if (isWithdrawClaimDestroyed) {
      toast({
        title: "Nothing left to withdraw",
        description: `Your ${withdrawPosition.symbol} share tokens are no longer in this wallet. Holding them is the claim, so this position can't be withdrawn.`,
        status: "info",
        duration: 8000,
        isClosable: true,
      });
      return;
    }

    const tokenAmount = parseFloat(withdrawAmount);
    if (isNaN(tokenAmount) || tokenAmount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const isFullExit = tokenAmount >= withdrawableAtUiPrecision;
    const remainingAfterWithdrawal = withdrawableAtUiPrecision - tokenAmount;
    if (!isFullExit && remainingAfterWithdrawal < withdrawMinDeposit) {
      toast({
        title: "Amount too large",
        description: `A withdrawal must leave at least ${withdrawMinDeposit} ${withdrawPosition.symbol} in your position, or withdraw your full position.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Mirror the program: shares = tokens * share_supply / staked. A full exit
    // burns the exact owned balance so no dust share is left behind.
    const requestedTokensRaw = BigInt(Math.floor(tokenAmount * 1e9));
    const requestedShares = Number(
      computeSharesForTokensRaw(
        requestedTokensRaw,
        BigInt(withdrawPosition.stakedRaw),
        BigInt(withdrawPosition.shareSupplyRaw)
      )
    );
    const sharesToRedeem = isFullExit
      ? withdrawPosition.sharesRaw
      : Math.min(withdrawPosition.sharesRaw, Math.max(0, requestedShares));

    if (sharesToRedeem <= 0) {
      toast({
        title: "Invalid amount",
        description: "Amount is too small to redeem.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await redeemFromWithdrawVault(sharesToRedeem, withdrawPosition.feeRecipient);

      toast({
        title: "Withdrawal successful!",
        description: `Successfully withdrew ${withdrawAmount} ${withdrawPosition.symbol} from drip`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setWithdrawAmount("");
      if (publicKey) {
        fetchPosition(publicKey);
        fetchPositions(publicKey);
        refetchDripHistory();
      }
    } catch (err) {
      const { isRejection, message } = describeWalletError(
        err,
        "The withdrawal transaction failed. Please try again."
      );
      toast({
        title: isRejection ? "Transaction cancelled" : "Withdrawal failed",
        description: message,
        status: isRejection ? "info" : "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const withdrawPercentage = (pct: number) => {
    // MAX would otherwise fill the field with an amount that can never redeem.
    if (!withdrawPosition || isWithdrawClaimDestroyed) return;
    const val = withdrawPosition.tokenValue * pct;
    // Floor instead of round so MAX/percent never exceeds owned shares.
    const floored = Math.floor(val * 1e6) / 1e6;
    setWithdrawAmount(floored.toFixed(6).replace(/\.?0+$/, ""));
  };

  return (
    <>
      {isPartnerPage && (
        <PartnerBackground
          accent={accentColor}
          accentRgb={accentRgb}
          watermark={partner!.watermark}
        />
      )}
      <Flex direction="column" minHeight="100vh">
        <Container maxW="600px" flex="1" py={8}>
          <VStack spacing={8} align="stretch">
            {partner ? (
              <DripPartnerHero
                partner={partner}
                iconSources={partnerIconSources}
                apy={MODERATE_APY}
                apyLoading={isApyLoading}
              />
            ) : (
              <DripPageHero apy={MODERATE_APY} apyLoading={isApyLoading} />
            )}

            <Box className="mint-box" w="100%">
              <DripSegmentTabs
                tabs={tabLabels}
                activeIndex={activeTab}
                onChange={setActiveTab}
              />

              {publicKey && (hasSuspendedNotice || hasDripAlerts) && (
                <VStack spacing={3} align="stretch" mt={4}>
                  {/* The reward token picker sits on this page, so only a
                      partner page needs a link out to it. */}
                  <SuspendedCampaignNotice
                    rewardTokenMints={heldRewardTokenMints}
                    changeTokenHref={isPartnerPage ? "/drip" : undefined}
                  />
                  {hasDripAlerts && (
                    <DripAlerts
                      alerts={dripHistory!.alerts!}
                      resolveSymbol={resolveRewardSymbol}
                    />
                  )}
                </VStack>
              )}

              <Box mt={4}>
                {activeTab === 0 && useMyPositionTabs && (
                        <VStack spacing={6} align="stretch">
                          {publicKey && hasAnyPosition ? (
                            <>
                              <DripPositionsCard
                                positions={mergedPositions}
                                isLoading={isPositionLoading || isAllPositionsLoading}
                              />

                              <Box p={4} {...dripSurfaceSx}>
                                <VStack spacing={3} align="stretch">
                                  <Text fontSize="sm" fontWeight="semibold" color="white">
                                    Rewards
                                  </Text>
                                  <DripRewardsChart
                                    history={dripRewardHistory}
                                    totalsByMint={dripRewardTotalsByMint}
                                    decimalsByMint={decimalsByMint}
                                    isLoading={isHistoryLoading || isDecimalsLoading}
                                    tokenMetaByMint={dripChartTokenMetaByMint}
                                    accentByMint={chartAccentByMint}
                                    pinnedMint={partner?.tokenMint}
                                    incentiveTotalsByMint={dripIncentiveTotalsByMint}
                                  />
                                </VStack>
                              </Box>
                            </>
                          ) : (
                            <Text color={DRIP_TEXT_LABEL} textAlign="center" py={6}>
                              {publicKey
                                ? "You have no active Drip position."
                                : "Connect your wallet to view your position."}
                            </Text>
                          )}
                        </VStack>
                )}

                {activeTab === depositTabIndex && (
                      <VStack spacing={6} align="stretch">
                        {activeVaultState?.paused && (
                          <Text fontSize="sm" color={ThemeColor.ACTION_ERROR} mb={3}>
                            The {activeVault.symbol} vault is paused. Deposits and
                            withdrawals are temporarily unavailable.
                          </Text>
                        )}

                        <StakeAmountField
                          label={hasVisiblePosition ? "Increase position" : "Deposit"}
                          variant="input"
                          tokenSelector={
                            <DripInputTokenPicker
                              options={inputAssetOptions}
                              selectedAsset={selectedAsset}
                              selectedAssetImage={selectedAssetImage}
                              onSelect={(asset, image) => {
                                setSelectedAsset(asset);
                                setSelectedAssetImage(image);
                                // Picking an asset means "I want to deposit it".
                                // Switching vaults re-derives the tab layout, so
                                // pin the selection to the deposit tab instead of
                                // letting the index keep whatever tab now sits
                                // there. The index is stable across the switch —
                                // the "My position" tab is gated on positions in
                                // ALL vaults, which an asset switch cannot change.
                                setActiveTab(depositTabIndex);
                              }}
                            />
                          }
                          amount={amount}
                          onAmountChange={(e) => {
                            const inputValue = e.target.value;
                            if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                              return;
                            }
                            setAmount(inputValue);
                          }}
                          headerRight={
                            publicKey ? (
                              <BalanceMaxHeader
                                displayedBalance={displayedBalance}
                                onMax={handleMaxClick}
                              />
                            ) : undefined
                          }
                        />

                        {isBelowMinimumAmount && (
                          <Text fontSize="xs" color="red.300" mt={-3}>
                            {hasExistingPosition
                              ? `Your total Drip position must be at least ${minDeposit} ${activeVault.symbol}. Top up so the total stays above the minimum — this ensures your rewards stream properly.`
                              : `Minimum deposit is ${minDeposit} ${selectedAsset}. This ensures your Drip rewards stream properly — smaller deposits generate rewards that are too small to pay out.`}
                          </Text>
                        )}

                        {!isPartnerPage && (
                        <>
                        <HStack spacing={2} alignItems="center">
                          <Text fontSize="sm" fontWeight="medium" color={DRIP_TEXT_SECONDARY}>
                            {hasVisiblePosition ? "Reward token" : "Invest yield in"}
                          </Text>
                          {hasVisiblePosition && (
                            <Tooltip
                              label="You can only DCA into one asset at a time for now."
                              hasArrow
                              placement="top"
                              bg="gray.700"
                              color="white"
                              fontSize="xs"
                            >
                              <IconButton
                                aria-label="Single DCA asset info"
                                icon={<InfoOutlineIcon boxSize="11px" />}
                                minW="18px"
                                w="18px"
                                h="18px"
                                p={0}
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: "gray.200", bg: "whiteAlpha.100" }}
                              />
                            </Tooltip>
                          )}
                        </HStack>

                        {/* Token selection cards */}
                        <VStack spacing={4} align="stretch">
                          <InputGroup size="md">
                            <InputLeftAddon
                              h="44px"
                              bg="whiteAlpha.100"
                              borderColor={DRIP_BORDER}
                            >
                              <SearchIcon color={DRIP_TEXT_LABEL} boxSize={3.5} />
                            </InputLeftAddon>
                            <Input
                              value={assetSearchQuery}
                              onChange={(e) => setAssetSearchQuery(e.target.value)}
                              placeholder="Search assets"
                              h="44px"
                              color="white"
                              bg={DRIP_BOX_BG}
                              borderColor={DRIP_BORDER}
                              _placeholder={{ color: DRIP_TEXT_MUTED }}
                            />
                            {assetSearchQuery && (
                              <InputRightElement h="44px" pr={2}>
                                <IconButton
                                  aria-label="Clear asset search"
                                  icon={<CloseIcon boxSize={2.5} />}
                                  size="xs"
                                  variant="ghost"
                                  color="gray.400"
                                  _hover={{ color: "white", bg: "whiteAlpha.200" }}
                                  onClick={() => setAssetSearchQuery("")}
                                />
                              </InputRightElement>
                            )}
                          </InputGroup>

                          {isLoadingKyrosTokens && (
                            <HStack spacing={2} color={DRIP_TEXT_LABEL}>
                              <Spinner size="xs" />
                              <Text fontSize="xs">Loading assets...</Text>
                            </HStack>
                          )}

                          {!isLoadingKyrosTokens && (
                            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                              {searchResultHelperText}
                            </Text>
                          )}

                          {featuredYieldToken && (
                            <Box
                              p={4}
                              borderRadius="xl"
                              border="1px solid"
                              borderColor={isFeaturedSelected ? ThemeColor.KAIROS : DRIP_BORDER}
                              bg={isFeaturedSelected ? "rgba(95, 244, 244, 0.08)" : DRIP_BOX_BG}
                              opacity={isOtherTokenSelected ? 0.75 : 1}
                              boxShadow={isFeaturedSelected ? "inset 0 0 0 1px rgba(95, 244, 244, 0.35)" : "none"}
                              transition="all 0.2s ease"
                              _hover={{
                                borderColor: ThemeColor.KAIROS,
                                bg: "rgba(95, 244, 244, 0.08)",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedBuyToken(featuredYieldToken);
                              }}
                            >
                              <Flex alignItems="center" justifyContent="space-between" gap={3} position="relative" zIndex={1}>
                                <HStack spacing={3}>
                                  <Image src={featuredYieldToken.icon} boxSize="32px" borderRadius="full" />
                                  <VStack spacing={0} align="start">
                                    <Text fontSize="lg" fontWeight="semibold" color="white">
                                      {featuredYieldToken.symbol}
                                    </Text>
                                    {featuredYieldToken.name && (
                                      <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                        {featuredYieldToken.name}
                                      </Text>
                                    )}
                                  </VStack>
                                </HStack>
                                {/* The 2x campaign is a KYROS perk, so the pill follows KYROS
                                    rather than whichever token happens to be featured. */}
                                {isFeaturedKyrosBoosted && (
                                  <HStack
                                    spacing={1}
                                    px={2}
                                    py="2px"
                                    borderRadius="full"
                                    bg="rgba(95, 244, 244, 0.2)"
                                    border="1px solid"
                                    borderColor="rgba(95, 244, 244, 0.45)"
                                  >
                                    <Text fontSize="xs" color={ThemeColor.KAIROS} fontWeight="bold">
                                      2x incentives
                                    </Text>
                                  </HStack>
                                )}
                              </Flex>
                            </Box>
                          )}

                          {isSearchResultTruncated && (
                            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                              Showing first {visibleSecondaryYieldTokens.length} results. Refine your search for more precision.
                            </Text>
                          )}

                          {shouldShowNoResultsState && (
                            <Box p={3} {...dripSurfaceSx}>
                              <Text fontSize="sm" color={DRIP_TEXT_LABEL}>
                                {hasNoSearchResults
                                  ? "No assets found. Try another symbol, name, or mint."
                                  : "No suggested assets available right now."}
                              </Text>
                            </Box>
                          )}

                          <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={4}>
                            {visibleSecondaryYieldTokens.map((token) => {
                              return (
                              <GridItem key={token.mint}>
                                <Box
                                  p={3}
                                  borderRadius="xl"
                                  border="1px solid"
                                  borderColor={selectedBuyToken?.mint === token.mint ? ThemeColor.KAIROS : DRIP_BORDER}
                                  bg={selectedBuyToken?.mint === token.mint ? "rgba(95, 244, 244, 0.06)" : DRIP_BOX_BG}
                                  opacity={selectedBuyToken?.mint === token.mint ? 1 : 0.7}
                                  boxShadow={selectedBuyToken?.mint === token.mint ? "inset 0 0 0 1px rgba(95, 244, 244, 0.35)" : "none"}
                                  transition="all 0.2s ease"
                                  _hover={{
                                    borderColor: ThemeColor.KAIROS,
                                    bg: "rgba(95, 244, 244, 0.06)",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setSelectedBuyToken(token);
                                  }}
                                >
                                  <Flex alignItems="center" justifyContent="space-between" gap={3}>
                                    <HStack spacing={3}>
                                      <Image src={token.icon} boxSize="32px" borderRadius="full" />
                                      <VStack spacing={0} align="start">
                                        <Text fontSize="sm" fontWeight="semibold" color={selectedBuyToken?.mint === token.mint ? "white" : DRIP_TEXT_LABEL}>
                                          {token.symbol}
                                        </Text>
                                        {token.name && (
                                          <Text fontSize="xs" color={DRIP_TEXT_MUTED} noOfLines={1} maxW="90px">
                                            {token.name}
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                  </Flex>
                                </Box>
                              </GridItem>
                              );
                            })}
                          </Grid>
                        </VStack>
                        </>
                        )}

                        {/* Expected Rewards Box */}
                        {shouldShowExpectedRewards && (
                          <Box
                            p={4}
                            borderRadius="xl"
                            border="1px solid"
                            borderColor={accentBg(0.25)}
                            bg={accentBg(0.05)}
                          >
                            <VStack spacing={3} align="stretch">
                              <Text fontSize="sm" fontWeight="semibold" color={DRIP_TEXT_SECONDARY}>
                                Expected rewards
                              </Text>
                              <Flex alignItems="center" gap={4}>
                                {isPartnerPage ? (
                                  <PartnerTokenIcon
                                    sources={partnerIconSources}
                                    label={partner!.tokenSymbol}
                                    accent={accentColor}
                                    boxSize="40px"
                                  />
                                ) : (
                                  <Image
                                    src={effectiveRewardTokenImage}
                                    boxSize="40px"
                                    borderRadius="full"
                                  />
                                )}
                                <VStack align="start" spacing={1} flex="1">
                                  {isLoadingPrices || !hasFreshExpectedRewards || !expectedRewards ? (
                                    <Skeleton height="32px" width="200px" />
                                  ) : (
                                    <>
                                      <HStack spacing={2} alignItems="baseline">
                                        {(() => {
                                          const rewardValue = formatSmartFixed6(
                                            expectedRewards.rewardTokenPerYear * rewardMultiplier
                                          );
                                          return (
                                            <Text
                                              // Odometer roll-in on the partner page: re-key on the value
                                              // so it remounts and replays the animation on each update.
                                              key={isPartnerPage ? rewardValue : undefined}
                                              className={isPartnerPage ? "partner-odo" : undefined}
                                              fontSize="2xl"
                                              fontWeight="semibold"
                                              color={accentColor}
                                            >
                                              {rewardValue}
                                            </Text>
                                          );
                                        })()}
                                        <Text fontSize="md" fontWeight="semibold" color={accentColor}>
                                          {effectiveRewardTokenName ?? "TOKEN"}
                                        </Text>
                                        <Text fontSize="xs" color={DRIP_TEXT_LABEL}>
                                          per year
                                        </Text>
                                      </HStack>
                                      {isKyrosRewardToken && (
                                        <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                          2x boost included
                                        </Text>
                                      )}
                                      {showBasedOnOneSolLabel && (
                                        <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                          Based on 1 SOL
                                        </Text>
                                      )}
                                      {showBasedOnYourDepositLabel && (
                                        <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                          Based on your {formatSmartFixed6(position.tokenValue)} {position.symbol} deposit
                                        </Text>
                                      )}
                                    </>
                                  )}
                                </VStack>
                              </Flex>
                            </VStack>
                          </Box>
                        )}

                        <Button
                          width="100%"
                          className="mint-box-button"
                          mt={2}
                          isDisabled={
                            !effectiveRewardTokenMintAddress ||
                            depositLoading ||
                            !publicKey ||
                            // See the withdraw button: a null vault state means
                            // `paused` is undefined, not false.
                            isActiveVaultStateLoading ||
                            activeVaultState?.paused ||
                            (hasPositiveAmount && (
                              isBelowMinimumAmount ||
                              isLoadingPrices ||
                              !expectedRewards ||
                              Number(amount) > Number(selectedAsset === "SOL" ? solBalance : tokenBalance)
                            )) ||
                            (!hasPositiveAmount && !rewardTokenChanged)
                          }
                          isLoading={depositLoading}
                          loadingText={
                            !hasPositiveAmount && hasVisiblePosition
                              ? "Updating reward token..."
                              : currentStep === 'converting'
                              ? 'Converting SOL to kySOL (1/2)...'
                              : currentStep === 'depositing'
                              // Only SOL needs a second transaction; every other
                              // input (including hSOL) deposits in one.
                              ? selectedAsset === 'SOL'
                                ? 'Depositing to Drip (2/2)...'
                                : 'Depositing to Drip...'
                              : 'Processing...'
                          }
                          onClick={handleDeposit}
                        >
                          {hasVisiblePosition
                            ? !hasPositiveAmount && rewardTokenChanged
                              ? isPartnerPage ? `Switch to ${partner!.tokenSymbol}` : "Update Reward Token"
                              : "Update Position"
                            : isPartnerPage ? `Drip ${partner!.tokenSymbol}` : "Drip it"}
                        </Button>
                      </VStack>
                )}

                {activeTab === withdrawTabIndex && (
                      <VStack spacing={6} align="stretch">
                        {!publicKey ? (
                          <Text color={DRIP_TEXT_LABEL} textAlign="center" py={8}>
                            Connect your wallet to withdraw.
                          </Text>
                        ) : withdrawPosition?.isValueUnavailable ? (
                          // An unreadable share supply leaves tokenValue at 0.
                          // Saying "no position" here would be a lie to a funded
                          // wallet, so name the failure instead.
                          <Box p={6} {...dripSurfaceSx}>
                            <VStack spacing={2} py={3}>
                              <Text color="white" fontWeight="semibold" textAlign="center">
                                Position value unavailable
                              </Text>
                              <Text color={DRIP_TEXT_LABEL} fontSize="sm" textAlign="center">
                                We couldn't read the {withdrawPosition?.symbol ?? ""} vault's share
                                supply, so your withdrawable amount can't be computed right now.
                                Your funds are safe — refresh and try again.
                              </Text>
                            </VStack>
                          </Box>
                        ) : isWithdrawClaimDestroyed ? (
                          // After the unavailable branch so a failed read is
                          // never reported as a lost claim, and before the "no
                          // position" one so the wallet learns why its balance
                          // is zero rather than that it never deposited.
                          <Box p={6} {...dripSurfaceSx}>
                            <VStack spacing={2} py={3}>
                              <Text color="white" fontWeight="semibold" textAlign="center">
                                Nothing left to withdraw
                              </Text>
                              <Text color={DRIP_TEXT_LABEL} fontSize="sm" textAlign="center">
                                Your {withdrawPosition?.symbol ?? ""} share tokens are no longer in
                                this wallet. Holding them is the claim, so this position can't be
                                withdrawn.
                              </Text>
                            </VStack>
                          </Box>
                        ) : !hasWithdrawablePosition ? (
                          <Box p={6} {...dripSurfaceSx}>
                            <VStack spacing={2} py={3}>
                              <Text color="white" fontWeight="semibold" textAlign="center">
                                No position to withdraw
                              </Text>
                              <Text color={DRIP_TEXT_LABEL} fontSize="sm" textAlign="center">
                                You don't have an active Drip position yet.
                              </Text>
                            </VStack>
                          </Box>
                        ) : (
                          <>
                            {withdrawVaultState?.paused && (
                              <Text fontSize="sm" color={ThemeColor.ACTION_ERROR} mb={3}>
                                The {withdrawVault.symbol} vault is paused. Deposits and
                                withdrawals are temporarily unavailable.
                              </Text>
                            )}

                            <StakeAmountField
                              label="Withdraw"
                              variant="input"
                              tokenSelector={
                                // Mirrors the deposit tab: the token slot itself is
                                // the selector. Only worth a menu when there is
                                // something to switch to, so a single-position
                                // wallet keeps the plain pill it has today.
                                allPositions.length > 1 ? (
                                  <DripInputTokenPicker
                                    options={withdrawAssetOptions}
                                    selectedAsset={withdrawPosition?.symbol ?? withdrawVault.symbol}
                                    selectedAssetImage={withdrawVaultIcon ?? ""}
                                    onSelect={(symbol) => {
                                      // Each vault holds a distinct deposit asset,
                                      // so a symbol identifies one position.
                                      const picked = allPositions.find(
                                        (p) => p.symbol === symbol
                                      );
                                      if (!picked) return;
                                      setWithdrawVaultId(picked.vaultId);
                                      setWithdrawAmount("");
                                    }}
                                  />
                                ) : (
                                  <TokenPill
                                    image={withdrawVaultIcon ?? ""}
                                    symbol={withdrawPosition?.symbol ?? withdrawVault.symbol}
                                  />
                                )
                              }
                              amount={withdrawAmount}
                              onAmountChange={(e) => {
                                const inputValue = e.target.value;
                                if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                                  return;
                                }
                                setWithdrawAmount(inputValue);
                              }}
                              headerRight={
                                <BalanceMaxHeader
                                  displayedBalance={`${formatSmartFixed6(withdrawPosition?.tokenValue ?? 0)} ${withdrawPosition?.symbol ?? ""}`}
                                  onMax={() => withdrawPercentage(1)}
                                />
                              }
                            />

                            <DripWithdrawPercentSelector onSelect={withdrawPercentage} />

                            {/* The field above already shows only the backed
                                amount, so say why it shrank. */}
                            {isWithdrawClaimShort && (
                              <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                Some of your {withdrawPosition?.symbol ?? ""} share tokens are no
                                longer in this wallet. Holding them is the claim, so only the amount
                                still held can be withdrawn.
                              </Text>
                            )}

                            {isBelowWithdrawMinimum && (
                              <Text fontSize="xs" color="red.300">
                                A withdrawal must leave at least {withdrawMinDeposit} {withdrawPosition?.symbol ?? ""} in your position, or withdraw your full position.
                              </Text>
                            )}

                            <Button
                              width="100%"
                              className="mint-box-button"
                              isDisabled={
                                !withdrawAmount ||
                                parseFloat(withdrawAmount) <= 0 ||
                                parseFloat(withdrawAmount) > (withdrawPosition?.tokenValue ?? 0) ||
                                isBelowWithdrawMinimum ||
                                // A stale read must never leave a clickable CTA.
                                isWithdrawClaimDestroyed ||
                                withdrawRedeemLoading ||
                                !publicKey ||
                                // Until the vault state lands, `paused` is
                                // undefined and a click on a paused vault pays a
                                // fee to revert with VaultPaused (6001).
                                isWithdrawVaultStateLoading ||
                                withdrawVaultState?.paused
                              }
                              isLoading={withdrawRedeemLoading}
                              loadingText="Withdrawing..."
                              onClick={handleWithdraw}
                            >
                              Withdraw
                            </Button>
                          </>
                        )}
                      </VStack>
                )}
              </Box>
            </Box>

            <Box p={6} {...dripSurfaceSx}>
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold" color="white">
                  FAQ
                </Text>

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    What is Drip?
                  </Text>
                  <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                    Drip lets you stake your SOL and automatically use the yield it generates to accumulate another asset over time. For example, you can stake SOL through Drip, choose ${dripExampleAsset} as your target asset, and let your MEV-boosted staking rewards be automatically converted into ${dripExampleAsset} each epoch.
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    When will my rewards be paid?
                  </Text>
                  <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                    Once you deposit, you will need to wait for 1 epoch until your deposit is active. From then, your rewards will stream to your wallet a couple of hours after the end of each epoch.
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    I deposited some SOL, the Drip transaction didn't go through and I can't see my SOL anymore.
                  </Text>
                  <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                    Your funds are SAFU. Your SOL were converted in kySOL. You can deposit them to Drip by selecting kySOL as the input token.
                  </Text>
                </Box>

                {!isPartnerPage && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="white">
                      Why can't I choose any token as the reward token?
                    </Text>
                    <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                      This is a first iteration of the Drip feature. We'll add more tokens soon.
                    </Text>
                  </Box>
                )}

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    Why was my reward paid in kySOL?
                  </Text>
                  <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                    Sometimes a reward for a period is too small to swap into your chosen token (or the token can't be swapped at that moment). When that happens, we pay the reward in kySOL instead. In that case the kySOL stays in your position and compounds, so you keep earning on it until it can be paid out in your chosen token.
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    Are there any fees?
                  </Text>
                  <Text fontSize="sm" color={DRIP_TEXT_LABEL} mt={1} lineHeight="1.5">
                    Nothing is charged on your deposit or withdrawal. Kyros keeps a small share of the rewards generated to run the strategy.
                  </Text>
                </Box>
              </VStack>
            </Box>

          </VStack>
        </Container>
      </Flex>
    </>
  );
};
